// Compiled from the Civil Unmanned Aircraft Rules, 2025 (S.R.O. 2459(I)/2025)
// Every constant here is traceable to a specific rule / schedule so forms can
// cite their source inline instead of presenting bare requirements.

export type Category = "I" | "II" | "III" | "IV";

export const CATEGORY_INFO: Record<
  Category,
  { label: string; weight: string; min: number; max: number | null; renewal: string; rule: string }
> = {
  I: {
    label: "Category I",
    weight: "≤ 250 g",
    min: 0,
    max: 250,
    renewal: "Every 3 years",
    rule: "Rule 3(1)(a)",
  },
  II: {
    label: "Category II",
    weight: "> 250 g – 5 kg",
    min: 250,
    max: 5000,
    renewal: "Every 3 years",
    rule: "Rule 3(1)(b)",
  },
  III: {
    label: "Category III",
    weight: "> 5 kg – 25 kg",
    min: 5000,
    max: 25000,
    renewal: "Every year",
    rule: "Rule 3(1)(c)",
  },
  IV: {
    label: "Category IV",
    weight: "> 25 kg – 100 kg",
    min: 25000,
    max: 100000,
    renewal: "Every year",
    rule: "Rule 3(1)(d)",
  },
};

export function categoryForWeightGrams(grams: number): Category | null {
  if (grams <= 0) return null;
  if (grams <= 250) return "I";
  if (grams <= 5000) return "II";
  if (grams <= 25000) return "III";
  if (grams <= 100000) return "IV";
  return null; // exceeds Category IV ceiling — outside registrable range
}

export const DECISION_WINDOWS = {
  ownerRegistration: { complete: 30, decide: 90, rule: "Rule 5(5)–(6)" },
  licence: { complete: 30, decide: 60, rule: "Rule 29(3)–(4)" },
  manufacturing: { complete: 30, decide: 90, rule: "Rule 34(4)–(5)" },
  importExport: { complete: 30, decide: 90, rule: "Rule 42(3)–(4)" },
  transfer: { complete: null, decide: null, rule: "Rule 7 / Second Schedule" },
};

export const FIRST_SCHEDULE_APPLICANT_FIELDS = [
  "Name of Individual/Company",
  "Address",
  "City",
  "District",
  "Province",
  "Country",
  "Email",
  "Mobile No.",
  "Date of Birth (18+ years)",
  "CNIC / SECP Registration No.",
  "Attachment: CNIC / SECP Registration document",
];

export const FIRST_SCHEDULE_UAS_I_II_FIELDS = [
  "Product Name",
  "Model",
  "Serial Number",
  "Maximum Take-Off Weight",
  "Documentary proof",
  "Purpose / usage of unmanned aircraft",
];

export const FIRST_SCHEDULE_UAS_III_IV_FIELDS = [
  "Product Name",
  "Model",
  "Serial Number",
  "Maximum Take-Off Weight",
  "Manufacturer Conformity Assessment + documentary proof",
  "Security Clearance",
  "Documentary proof",
  "Purpose / usage of unmanned aircraft",
];

export const SECOND_SCHEDULE_TRANSFER_FIELDS = [
  "Name of Individual/Company",
  "Address",
  "City",
  "District",
  "Province",
  "Country",
  "Email",
  "Mobile No.",
  "Age",
  "CNIC / SECP Registration No.",
  "Product Name",
  "Model",
  "Serial Number",
  "Maximum Take-Off Weight",
  "Transfer Statement",
  "Notary Attestation",
];

export const THIRD_SCHEDULE_MANUFACTURING_FIELDS = [
  "Name of Individual/Company",
  "Address",
  "City",
  "District",
  "Province",
  "Country",
  "Email",
  "Mobile No.",
  "Age",
  "CNIC / SECP Registration No.",
  "Documentary proof",
];

export const FOURTH_SCHEDULE_IMPORT_EXPORT_FIELDS = [
  "Name of Individual/Company",
  "Address",
  "City",
  "District",
  "Province",
  "Country",
  "Email",
  "Mobile No.",
  "Age",
  "CNIC / SECP Registration No.",
  "Documentary proof",
];

export const FAIR_USE_OPTIONS = [
  "Film and media production",
  "Agriculture",
  "Inspections",
  "Surveys",
  "Photography and videography",
  "Recreation",
  "Disaster relief",
  "Research and development",
  "Search and rescue",
  "Delivery and transport",
];

// Safe Drone Usage Committee (SDUC) — constituted at district level by the
// Punjab Home Department, Notification No.SO(IS-II)3-15/2026(Drone), 7 July 2026.
export const SDUC_COMMITTEE = [
  { role: "Deputy Commissioner", designation: "Convener" },
  { role: "ADC (General) of the district concerned", designation: "Member/Secretary" },
  { role: "Representative from the Police Department (not below the rank of SP)", designation: "Member" },
  { role: "Representative from the CTD", designation: "Member" },
  { role: "Representative from the Special Branch", designation: "Member" },
  { role: "Head of the Requesting Department", designation: "Member" },
  { role: "Any other member co-opted by the Convener", designation: "Member" },
];

export const SDUC_TOR = [
  { key: "a", text: "Determine the specified area(s), duration and timing for the proposed use of drones as requested by any government agency or private entity." },
  { key: "b", text: "Ensure prior intimation to the concerned local police authorities for coordination, security arrangements and avoiding damage to persons/property." },
  { key: "c", text: "Take any other appropriate decision for safe usage of drones for agricultural, irrigation, other public purposes, public welfare or research." },
];

export const PUNJAB_DISTRICTS = [
  "Lahore", "Rawalpindi", "Faisalabad", "Multan", "Gujranwala", "Sialkot",
  "Bahawalpur", "Sargodha", "Sheikhupura", "Rahim Yar Khan", "Jhang", "Dera Ghazi Khan",
  "Gujrat", "Kasur", "Okara", "Sahiwal", "Chiniot", "Vehari", "Muzaffargarh",
];

export const APPLICATION_TYPES = [
  { key: "owner", label: "Owner / User Registration", rule: "Rule 5, First Schedule" },
  { key: "uas", label: "Unmanned Aircraft Registration", rule: "Rule 4–6, First Schedule" },
  { key: "transfer", label: "Transfer of Ownership", rule: "Rule 7, Second Schedule" },
  { key: "manufacturing", label: "Design / Manufacturing / Repair Certificate", rule: "Rule 33–34, Third Schedule" },
  { key: "importExport", label: "Importer / Exporter Registration", rule: "Rule 41–42, Fourth Schedule" },
  { key: "sduc", label: "Drone Usage Approval (SDUC)", rule: "Home Dept. Notification, 7 Jul 2026" },
] as const;

export type ApplicationTypeKey = (typeof APPLICATION_TYPES)[number]["key"];

export const STATUS_STEPS = ["Draft", "Submitted", "Under Review", "Decided"] as const;
export type Status = (typeof STATUS_STEPS)[number];
